import { supabase } from "@/integrations/supabase/client";
import { ApiClient } from "./client";
import { toast } from "sonner";

export interface SocialCounts {
  likes_count: number;
  favorites_count: number;
  comments_count: number;
}

export interface Comment {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  derivative_id: string;
  parent_id: string | null;
  is_edited: boolean;
  creator?: {
    name: string;
    avatar?: string;
    is_academy?: boolean;
  };
}

// In-memory cache with 5-minute expiration for social counts
interface SocialCountsCache {
  [key: string]: {
    data: SocialCounts;
    timestamp: number;
  };
}

const socialCountsCache: SocialCountsCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class SocialApi extends ApiClient {
  // Like functions
  async likeDerivative(derivativeId: string) {
    try {
      // Get user ID first to ensure authentication
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return { success: false };
      }
      
      const { data: existingLike } = await supabase
        .from('derivative_likes')
        .select('id')
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId)
        .single();

      if (existingLike) {
        // User already liked this derivative, unlike it
        return this.unlikeDerivative(derivativeId);
      }

      const { error } = await supabase
        .from('derivative_likes')
        .insert({
          derivative_id: derivativeId,
          user_id: userId,
        });

      if (error) throw error;
      return { success: true, action: 'liked' };
    } catch (error) {
      console.error('Error liking derivative:', error);
      toast.error('Erreur lors de l\'action "J\'aime"');
      return { success: false };
    }
  }

  async unlikeDerivative(derivativeId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return { success: false };
      }
      
      const { error } = await supabase
        .from('derivative_likes')
        .delete()
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true, action: 'unliked' };
    } catch (error) {
      console.error('Error unliking derivative:', error);
      toast.error('Erreur lors de la suppression du "J\'aime"');
      return { success: false };
    }
  }

  async checkIfLiked(derivativeId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) return false;
      
      const { data, error } = await supabase
        .from('derivative_likes')
        .select('id')
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking if derivative is liked:', error);
      return false;
    }
  }

  // Favorite functions
  async favoriteDerivative(derivativeId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return { success: false };
      }
      
      const { data: existingFavorite } = await supabase
        .from('derivative_favorites')
        .select('id')
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId)
        .single();

      if (existingFavorite) {
        // User already favorited this derivative, unfavorite it
        return this.unfavoriteDerivative(derivativeId);
      }

      const { error } = await supabase
        .from('derivative_favorites')
        .insert({
          derivative_id: derivativeId,
          user_id: userId,
        });

      if (error) throw error;
      return { success: true, action: 'favorited' };
    } catch (error) {
      console.error('Error favoriting derivative:', error);
      toast.error('Erreur lors de l\'ajout aux favoris');
      return { success: false };
    }
  }

  async unfavoriteDerivative(derivativeId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return { success: false };
      }
      
      const { error } = await supabase
        .from('derivative_favorites')
        .delete()
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true, action: 'unfavorited' };
    } catch (error) {
      console.error('Error unfavoriting derivative:', error);
      toast.error('Erreur lors de la suppression des favoris');
      return { success: false };
    }
  }

  async checkIfFavorited(derivativeId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) return false;
      
      const { data, error } = await supabase
        .from('derivative_favorites')
        .select('id')
        .eq('derivative_id', derivativeId)
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking if derivative is favorited:', error);
      return false;
    }
  }

  // Comment functions
  async addComment(derivativeId: string, content: string, parentId?: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour commenter");
        return { success: false, data: null };
      }
      
      const { data, error } = await supabase
        .from('derivative_comments')
        .insert({
          derivative_id: derivativeId,
          user_id: userId,
          content,
          parent_id: parentId || null,
        })
        .select('*')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
      return { success: false, data: null };
    }
  }

  async updateComment(commentId: string, content: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour modifier un commentaire");
        return { success: false, data: null };
      }
      
      const { data, error } = await supabase
        .from('derivative_comments')
        .update({ content, is_edited: true })
        .eq('id', commentId)
        .eq('user_id', userId)
        .select('*')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Erreur lors de la mise à jour du commentaire');
      return { success: false, data: null };
    }
  }

  async deleteComment(commentId: string) {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour supprimer un commentaire");
        return { success: false };
      }
      
      const { error } = await supabase
        .from('derivative_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Erreur lors de la suppression du commentaire');
      return { success: false };
    }
  }

  async getComments(derivativeId: string): Promise<Comment[]> {
    try {
      console.log(`Fetching comments for derivative ${derivativeId}`);
      
      const { data, error } = await supabase
        .from('derivative_comments')
        .select('*')
        .eq('derivative_id', derivativeId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      console.log(`Found ${data?.length || 0} comments for derivative ${derivativeId}`);
      
      // Get user profiles for comments
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(comment => comment.user_id))];
        
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url, is_academy')
          .in('id', userIds);

        const profileMap = new Map();
        if (profiles) {
          profiles.forEach(profile => {
            profileMap.set(profile.id, {
              name: profile.full_name || profile.username || 'Utilisateur',
              avatar: profile.avatar_url,
              is_academy: profile.is_academy
            });
          });
        }

        // Add creator info to comments
        return data.map(comment => ({
          ...comment,
          creator: profileMap.get(comment.user_id) || {
            name: 'Utilisateur inconnu',
            is_academy: false
          }
        }));
      }
      
      return data || [];
    } catch (error) {
      console.error('Error getting comments:', error);
      return [];
    }
  }

  // NEW FUNCTION: Get user favorites with detailed derivative information
  async getUserFavorites() {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        toast.error("Vous devez être connecté pour voir vos favoris");
        return { data: [], error: "Authentication required" };
      }
      
      // Get user's favorite derivatives with their details
      const { data, error } = await supabase
        .from('derivative_favorites')
        .select(`
          id,
          derivative_id,
          created_at,
          derivatives_with_details!inner (
            id, title, description, thumbnail, video_url,
            expression_type, aspect_ratio, creator_id, creator_name,
            creator_avatar, clip_id, original_title, status,
            submitted_at, approved_at, rejected_at, rejection_reason,
            likes_count, favorites_count, comments_count, views
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user favorites:', error);
        return { data: [], error };
      }

      // Transform the data to a more usable format
      const transformedData = data?.map(item => ({
        favoriteId: item.id,
        favoriteCreatedAt: item.created_at,
        ...item.derivatives_with_details
      })) || [];
      
      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error in getUserFavorites:', error);
      return { data: [], error };
    }
  }

  // New batch get method for efficiently getting multiple social counts
  async getBatchSocialCounts(derivativeIds: string[]): Promise<Record<string, SocialCounts>> {
    try {
      if (!derivativeIds.length) return {};
      
      // Filter out IDs that are in the cache and still valid
      const now = Date.now();
      const idsToFetch = derivativeIds.filter(id => {
        const cachedItem = socialCountsCache[id];
        return !cachedItem || now - cachedItem.timestamp > CACHE_DURATION;
      });
      
      // Initialize result with cached values
      const result: Record<string, SocialCounts> = {};
      derivativeIds.forEach(id => {
        const cachedItem = socialCountsCache[id];
        if (cachedItem && now - cachedItem.timestamp <= CACHE_DURATION) {
          result[id] = cachedItem.data;
        }
      });
      
      // If all values are in cache, return them
      if (idsToFetch.length === 0) {
        return result;
      }
      
      // Fetch missing data from database
      const { data, error } = await supabase
        .from('derivatives')
        .select('id, likes_count, favorites_count, comments_count')
        .in('id', idsToFetch);
      
      if (error) {
        console.error(`Error batch fetching social counts:`, error);
        return result;
      }
      
      // Update result and cache with fetched data
      if (data && data.length > 0) {
        data.forEach(item => {
          const counts = {
            likes_count: item.likes_count || 0,
            favorites_count: item.favorites_count || 0,
            comments_count: item.comments_count || 0
          };
          
          result[item.id] = counts;
          socialCountsCache[item.id] = {
            data: counts,
            timestamp: now
          };
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error batch getting social counts:', error);
      return {};
    }
  }

  async getSocialCounts(derivativeId: string): Promise<SocialCounts> {
    try {
      // Check cache first
      const now = Date.now();
      const cachedItem = socialCountsCache[derivativeId];
      if (cachedItem && now - cachedItem.timestamp <= CACHE_DURATION) {
        return cachedItem.data;
      }
      
      console.log(`Fetching social counts for derivative ${derivativeId}`);
      
      const { data, error } = await supabase
        .from('derivatives')
        .select('likes_count, favorites_count, comments_count')
        .eq('id', derivativeId)
        .single();

      if (error) {
        console.error(`Error fetching counts for ${derivativeId}:`, error);
        throw error;
      }
      
      console.log(`Social counts for ${derivativeId}:`, data);
      
      const counts = {
        likes_count: data.likes_count || 0,
        favorites_count: data.favorites_count || 0,
        comments_count: data.comments_count || 0
      };
      
      // Update cache
      socialCountsCache[derivativeId] = {
        data: counts,
        timestamp: now
      };
      
      return counts;
    } catch (error) {
      console.error('Error getting social counts:', error);
      return {
        likes_count: 0,
        favorites_count: 0,
        comments_count: 0
      };
    }
  }

  // Helper functions
  private async isAuthenticated() {
    const userId = await this.getUserId();
    return !!userId;
  }

  private async getUserId() {
    try {
      const { data } = await supabase.auth.getUser();
      return data?.user?.id;
    } catch (error) {
      console.error("Error getting user ID:", error);
      return undefined;
    }
  }
}

export const socialApi = new SocialApi();
